'use client';

import { useId } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { Numeric } from '@/components/ui/numeric';
import { cn } from '@/lib/utils';
import type { DrawMatch } from '@/draw/types';
import { DrawPlayerLink } from '@/app/[locale]/event/draw/components/DrawPlayerLink';
import { NAME_WRAP } from '@/app/[locale]/event/[eventId]/libs/nameBreaks';
import { NameText } from '@/app/[locale]/event/[eventId]/[slug]/components/NameText';
import { displayMatchCode } from '@/app/[locale]/event/[eventId]/libs/matchCode';
import { matchGames } from '../libs/poolGrid';

const formatWhen = (iso: string, locale: string, timeZone?: string) =>
  new Intl.DateTimeFormat(locale, { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(iso));

interface PoolUpcomingProps {
  matches: DrawMatch[];
  total: number;
  /** Standings names by player id, so the list reads like the grid. */
  names: Map<string, string>;
  playerHref?: (id: string) => string | undefined;
}

export function PoolUpcoming({ matches, total, names, playerHref }: PoolUpcomingProps) {
  const t = useTranslations('Event.drawView.pool');
  const locale = useLocale();
  const headingId = useId();
  if (matches.length === 0) return null;

  const side = (p: DrawMatch['player1']) => {
    if (!p) return null;
    const name = names.get(p.id) ?? p.displayName ?? `${p.firstName} ${p.lastName}`.trim();
    return (
      <DrawPlayerLink playerId={p.id} playerHref={playerHref}>
        <NameText name={name} />
      </DrawPlayerLink>
    );
  };

  return (
    <section aria-labelledby={headingId} className="border-border mt-3 overflow-hidden rounded-lg border">
      <h4 id={headingId} className="bg-surface border-border text-muted flex border-b px-3 py-1.5 text-[11px] font-semibold">
        <span>{t('stillToPlay')}</span>
        <span className="ml-auto font-medium">{t('ofTotal', { left: matches.length, total })}</span>
      </h4>
      <ul>
        {matches.map((m) => {
          const live = m.status === 'in_progress';
          const iso = m.eventMatchSchedule?.scheduled_datetime;
          const games = matchGames(m);
          const court = m.eventMatchSchedule?.court_name;
          const code = displayMatchCode(m.matchId);
          return (
            <li
              key={m.matchId}
              className="border-border grid min-h-[38px] grid-cols-[72px_1fr_auto_1fr] items-center gap-x-2.5 border-t px-3 py-1 text-[13px] first:border-t-0 @min-[640px]:grid-cols-[96px_1fr_auto_1fr_90px]"
            >
              <span className={cn('text-[11.5px] leading-4', live ? 'text-danger-emphasis font-bold' : 'text-muted')}>
                {code && <span data-match-code className="text-foreground block font-mono text-[10.5px] font-semibold tracking-wider">{code}</span>}
                {live ? (
                  <>
                    <span aria-hidden>● </span>
                    {t('liveCap')}
                  </>
                ) : iso ? (
                  <LocalDateTime iso={iso} locale={locale} format={formatWhen} />
                ) : (
                  '–'
                )}
              </span>
              <span className={cn('min-w-0 text-right font-medium', NAME_WRAP)}>{side(m.player1)}</span>
              <span className={cn('min-w-10 text-center', live ? 'text-foreground text-[13px] font-bold' : 'text-muted text-[11px]')}>
                {live ? <Numeric>{`${games.player1}–${games.player2}`}</Numeric> : t('vs')}
              </span>
              <span className={cn('min-w-0 font-medium', NAME_WRAP)}>{side(m.player2)}</span>
              <span className="text-muted hidden text-right text-[11.5px] @min-[640px]:block">{court ?? ''}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
